import { Chip, TableTooltip } from "@nivo/tooltip";
import { Divider, Typography } from "@mui/material";
import { Serie, SliceTooltipProps } from "@nivo/line";
import { SelectedTimeValues } from "src/components/monitoring/senec-line-chart/lineChartTypes";
import { JSX, useEffect } from "react";
import { dateFormatterTooltip } from "src/components/monitoring/senec-line-chart/lineChartUtils";

type SenecTooltipProps = SliceTooltipProps & {
    selectedLegendTypes: string[];
    data: Serie[];
    locale: Intl.Locale;
    onSelectedTimeValues?: (selectedTimeValues: SelectedTimeValues) => void;
    tooltipValueMapper?: (value: number | string, id: number | string) => string;
    showTooltip: boolean;
    customTooltipDateFormat?: Intl.DateTimeFormatOptions;
};
export const SenecLineChartTooltip = ({
    slice,
    axis,
    data,
    locale,
    selectedLegendTypes,
    showTooltip,
    customTooltipDateFormat,
    onSelectedTimeValues,
    tooltipValueMapper,
}: SenecTooltipProps): null | JSX.Element => {
    useSelectedTimeValueChange({ slice, axis }, onSelectedTimeValues);

    if (!showTooltip) {
        return null;
    }

    return (
        <TableTooltip
            title={
                <>
                    <Typography fontWeight="bold" p="0.5rem 0.25rem">
                        {dateFormatterTooltip(locale.toString(), customTooltipDateFormat).format(
                            new Date(slice.points[0].data.x),
                        )}
                    </Typography>
                    <Divider sx={{ mb: "0.5rem" }} />
                </>
            }
            rows={slice.points
                .filter((point) => selectedLegendTypes.includes(point.serieId.toString()))
                .sort((p1, p2) => p1.index - p2.index)
                .map((point) => [
                    <Chip key={`${point.id}_chip`} color={point.color} style={{ marginBottom: "3px" }} />,
                    <Typography key={`${point.id}_label`}>
                        {data.filter((elem) => elem.id === point.serieId.toString())[0].label}
                    </Typography>,
                    <Typography key={`${point.id}_value`} fontWeight="bold" align="right">
                        {tooltipValueMapper === undefined
                            ? point.data.yFormatted.toString()
                            : tooltipValueMapper(point.data.yFormatted, point.serieId)}
                    </Typography>,
                ])}
        />
    );
};

const useSelectedTimeValueChange = (
    tooltipProps: SliceTooltipProps,
    onSelectedTimeValues?: (selectedTimeValues: SelectedTimeValues) => void,
) => {
    const slice = tooltipProps.slice;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const values: Record<string, number>[] = slice.points.map((point) => ({
        [point.serieId]: point.data.y,
    }));

    useEffect(() => {
        if (onSelectedTimeValues) {
            onSelectedTimeValues({ selectedTime: slice.points[0].data.x.toString(), values: values });
        }
    }, [slice, onSelectedTimeValues, values]);
};
